import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { apiPost, apiPut } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminFormDialog, AdminField } from "./AdminFormDialog";
import { Input, SelectNative } from "../ui/input";

export type UserFormState = {
  staffId: string;
  name: string;
  email: string;
  password: string;
  role: string;
  facultyId: string;
  centerId: string;
  isActive: boolean;
};

export const EMPTY_USER: UserFormState = {
  staffId: "", name: "", email: "", password: "", role: "faculty_publisher",
  facultyId: "", centerId: "", isActive: true,
};

const firstEmail = (member: any): string => {
  const emails = member?.email;
  if (Array.isArray(emails) && emails.length) return String(emails[0]).trim();
  return "";
};

type Props = {
  open: boolean;
  editing: any | null;
  faculties: any[];
  centers: any[];
  staffMembers: any[];
  onClose: () => void;
  onSaved: () => void;
};

const FORM_ID = "user-form";

export const AdminUserModal: React.FC<Props> = ({
  open, editing, faculties, centers, staffMembers, onClose, onSaved,
}) => {
  const { t } = useLanguage();
  const requireStaffPick = !editing && staffMembers.length > 0;

  const schema = Yup.object({
    staffId: requireStaffPick ? Yup.string().required(t.admin.selectStaffMember) : Yup.string(),
    name: Yup.string().trim().required(t.common.required),
    email: Yup.string().trim().email("Email invalide").required(t.common.required),
    password: editing
      ? Yup.string().test("pw", t.admin.passwordRequired, (v) => !v || v.length >= 6)
      : Yup.string().min(6, t.admin.passwordRequired).required(t.admin.passwordRequired),
    role: Yup.string().oneOf(["faculty_publisher", "center_publisher", "super_admin"]).required(),
    facultyId: Yup.string().when("role", {
      is: "faculty_publisher",
      then: (s) => s.required(t.admin.selectFaculty),
      otherwise: (s) => s.notRequired(),
    }),
    centerId: Yup.string().when("role", {
      is: "center_publisher",
      then: (s) => s.required(t.admin.selectCenter),
      otherwise: (s) => s.notRequired(),
    }),
    isActive: Yup.boolean(),
  });

  const initial: UserFormState = editing ? {
    staffId: editing.staffId ? String(editing.staffId) : "",
    name: editing.name || "",
    email: editing.email || "",
    password: "",
    role: editing.role || "faculty_publisher",
    facultyId: editing.facultyId ? String(editing.facultyId) : "",
    centerId: editing.centerId ? String(editing.centerId) : "",
    isActive: editing.isActive !== false,
  } : EMPTY_USER;

  return (
    <Formik
      initialValues={initial}
      validationSchema={schema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        try {
          if (editing) {
            const payload: any = {
              name: values.name.trim(),
              email: values.email.trim(),
              role: values.role,
              facultyId: values.role === "faculty_publisher" && values.facultyId ? Number(values.facultyId) : null,
              centerId: values.role === "center_publisher" && values.centerId ? Number(values.centerId) : null,
              staffId: values.staffId ? Number(values.staffId) : null,
              isActive: values.isActive,
            };
            if (values.password.trim()) payload.password = values.password;
            await apiPut(`/users/${editing.id}`, payload);
          } else {
            await apiPost("/auth/register", {
              name: values.name.trim(),
              email: values.email.trim(),
              password: values.password,
              role: values.role,
              facultyId: values.role === "faculty_publisher" && values.facultyId ? Number(values.facultyId) : null,
              centerId: values.role === "center_publisher" && values.centerId ? Number(values.centerId) : null,
              staffId: values.staffId ? Number(values.staffId) : null,
            });
          }
          toast.success(t.common.saveSuccess);
          onSaved();
          onClose();
        } catch (e: any) {
          toast.error(e?.response?.data?.reason || e?.messageText || t.common.error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <AdminFormDialog
          open={open}
          onClose={onClose}
          title={editing ? t.admin.editUser : t.admin.newUser}
          subtitle={t.admin.userFormSubtitle}
          testId="user-modal"
          className="max-w-md"
          formId={FORM_ID}
          submitLabel={editing ? t.admin.save : t.admin.create}
          isSubmitting={isSubmitting}
        >
          <Form id={FORM_ID} className="grid gap-4 pb-4">
            {!editing && staffMembers.length > 0 && (
              <AdminField label={t.admin.staff} required error={touched.staffId ? errors.staffId : undefined}>
                <SelectNative
                  id="user-staff"
                  data-testid="user-staff-select"
                  value={values.staffId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setFieldValue("staffId", id);
                    const member = staffMembers.find((s) => String(s.id) === id);
                    if (member) {
                      setFieldValue("name", member.name || "");
                      const mail = firstEmail(member);
                      if (mail) setFieldValue("email", mail);
                    }
                  }}
                >
                  <option value="">{t.admin.selectStaffMember}</option>
                  {staffMembers.map((s) => (
                    <option key={s.id} value={s.id}>{s.name} — {s.role}</option>
                  ))}
                </SelectNative>
              </AdminField>
            )}
            <AdminField label={t.admin.name} required error={touched.name ? errors.name : undefined}>
              <Field as={Input} id="user-name" name="name" data-testid="user-name-input" />
            </AdminField>
            <AdminField label={t.admin.email} required error={touched.email ? errors.email : undefined}>
              <Field as={Input} id="user-email" name="email" type="email" data-testid="user-email-input" disabled={!!editing} />
            </AdminField>
            <AdminField label={editing ? t.admin.passwordOptional : t.admin.passwordRequired} error={touched.password ? errors.password : undefined}>
              <Field as={Input} id="user-password" name="password" type="password" data-testid="user-password-input" />
            </AdminField>
            <AdminField label={t.admin.role}>
              <Field
                as={SelectNative}
                id="user-role"
                name="role"
                data-testid="user-role-select"
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const role = e.target.value;
                  setFieldValue("role", role);
                  if (role !== "faculty_publisher") setFieldValue("facultyId", "");
                  if (role !== "center_publisher") setFieldValue("centerId", "");
                }}
              >
                <option value="faculty_publisher">{t.admin.rolePublisher}</option>
                <option value="center_publisher">{t.admin.roleCenterPublisher}</option>
                <option value="super_admin">{t.admin.roleAdmin}</option>
              </Field>
            </AdminField>
            {values.role === "faculty_publisher" && (
              <AdminField label={t.admin.faculty} required error={touched.facultyId ? errors.facultyId : undefined}>
                <Field as={SelectNative} id="user-faculty" name="facultyId" data-testid="user-faculty-select">
                  <option value="">{t.admin.selectFaculty}</option>
                  {faculties.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </Field>
              </AdminField>
            )}
            {values.role === "center_publisher" && (
              <AdminField label={t.admin.researchCenters} required error={touched.centerId ? errors.centerId : undefined}>
                <Field as={SelectNative} id="user-center" name="centerId" data-testid="user-center-select">
                  <option value="">{t.admin.selectCenter}</option>
                  {centers.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </Field>
              </AdminField>
            )}
            {editing && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={values.isActive} onChange={(e) => setFieldValue("isActive", e.target.checked)} />
                {values.isActive ? t.admin.active : t.admin.inactive}
              </label>
            )}
          </Form>
        </AdminFormDialog>
      )}
    </Formik>
  );
};
